import { MenuProduct } from '@meniudigital/shared'
import { useEffect, useState } from 'react'
import { EmptyProductProps } from './types'

// A Sunburst retouch at 2K takes 30 to 45 seconds end to end, measured on the first jobs; 50 leaves slack
const RETOUCH_TYPICAL_MS = 50 * 1000

// Grows to 90% over the typical job time, then creeps towards 98% so a slow job still looks alive
export const getRetouchProgress = (startedAt: string | undefined, now: number) => {
  if (!startedAt) {
    return 0
  }

  const ratio = (now - new Date(startedAt).getTime()) / RETOUCH_TYPICAL_MS

  if (ratio < 1) {
    return Math.max(0, ratio * 90)
  }

  return Math.min(98, 90 + (ratio - 1) * 8)
}

export const generateEmptyProduct = (props?: EmptyProductProps) => {
  const emptyProduct: MenuProduct & { genericImageName?: string } = {
    id: Math.random() * 100 + 'randomIdBeforeCreation',
    userId: '',
    index: props?.index || 0,
    names: { ro: '' },
    descriptions: { ro: '' },
    imageUrl: '',
    imageKey: '',
    quantities: props?.quantities || '',
    kcalories: props?.kcalories || '',
    // @ts-ignore
    price: props?.price || '',
    isAvailable: true,
    isHidden: false,
    isDiscounted: false,
    isRetouchEnabled: true,
    categoryId: props?.categoryId || '',
    allergens: [],
    nutritionalDeclaration: '',
    childProducts: [],
    parentProductId: '',
    genericImageName: '',
  }

  return emptyProduct
}

export function isWhitePixel(x: number, y: number, data: Uint8ClampedArray, imageWidth: number) {
  const red = data[(imageWidth * y + x) * 4]
  const green = data[(imageWidth * y + x) * 4 + 1]
  const blue = data[(imageWidth * y + x) * 4 + 2]
  const alpha = data[(imageWidth * y + x) * 4 + 3]

  if (alpha < 5) {
    return true
  }

  const minValueForWhite = 245
  const currentWhiteValue = (red + green + blue) / 3

  if (currentWhiteValue >= minValueForWhite) {
    return true
  }

  return false
}

export function hasWhiteBackground(image: HTMLImageElement): Promise<boolean> {
  return new Promise(async (resolve, _) => {
    const imageObj = new Image()
    imageObj.onload = function () {
      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')!
      const imageWidth = imageObj.width
      const imageHeight = imageObj.height
      canvas.width = imageWidth
      canvas.height = imageHeight

      context.drawImage(imageObj, 0, 0)

      const imageData = context.getImageData(0, 0, imageWidth, imageHeight)

      // To ignore white border
      const cornerOffset = 13

      const whitePoints = [
        // Corners
        isWhitePixel(cornerOffset, cornerOffset, imageData.data, imageWidth),
        isWhitePixel(imageWidth - cornerOffset - 1, cornerOffset, imageData.data, imageWidth),
        isWhitePixel(cornerOffset, imageHeight - 1, imageData.data, imageWidth),
        isWhitePixel(imageWidth - cornerOffset - 1, imageHeight - cornerOffset - 1, imageData.data, imageWidth),

        // Middle points
        // isBasicallyWhite(imageWidth / 2, 0, imageData.data, imageWidth),
        // isBasicallyWhite(0, imageHeight / 2, imageData.data, imageWidth),
        isWhitePixel(imageWidth - 1, imageHeight / 2, imageData.data, imageWidth),
        isWhitePixel(imageWidth / 2, imageHeight - 1, imageData.data, imageWidth),
      ]

      const hasWhitePadding = whitePoints.filter(x => !x).length <= 2

      resolve(hasWhitePadding)
    }
    imageObj.crossOrigin = 'Anonymous'
    imageObj.src = image.src
  })
}

// The full-size photo takes a moment to arrive; the thumbnail is already cached from the list, so it fills
// the frame meanwhile and the full one takes over once it has loaded
export function useLoadedSrc(fullSrc?: string, previewSrc?: string) {
  const [loadedSrc, setLoadedSrc] = useState('')

  useEffect(() => {
    if (!fullSrc || !previewSrc) {
      return
    }

    let isCurrent = true
    const image = new Image()
    image.onload = () => {
      if (isCurrent) {
        setLoadedSrc(fullSrc)
      }
    }
    image.src = fullSrc

    return () => {
      isCurrent = false
    }
  }, [fullSrc, previewSrc])

  return previewSrc && loadedSrc !== fullSrc ? previewSrc : fullSrc
}
